import * as z from 'zod';
import { MAX_ACTIVE_IMAGES_PER_PERSON, MAX_ADHOC_FIELDS_PER_PERSON, MAX_NOTES_LENGTH, } from '../limits.js';
import { auditStampSchema, countryCodeSchema, idempotencyKeySchema, isoDateTimeSchema, machineKeySchema, recordActionMethodSchema, shortRefSchema, strict, uuidSchema, versionSchema, } from './common.js';
import { issuedCredentialSchema, passCodeSchema } from './credential.js';
import { fieldValueSchema } from './field.js';
import { numberedPageSchema, paginationQuerySchema } from './pagination.js';
/**
 * The person: the record everything else in this system hangs off.
 *
 * **Field values arrive as a map, not as named properties.** AD-16 makes core and admin-defined
 * fields the same thing, so a create request carries `fields: { first_name: ..., ... }` and the
 * active field definitions decide what is valid. {@link personFieldsSchema} builds that validator
 * from the definitions; this file's static schemas pin everything around it.
 *
 * **Modification never deletes (AD-25) and never silently overwrites (AD-15).** Every update
 * carries the `version` it was read at, and a stale one is a conflict the operator resolves rather
 * than a lost edit.
 *
 * **The three product caps appear here as schema constraints**, and identically as a CHECK
 * constraint and a trigger in the database. All three derive from the same constants, and a `db`
 * test compares them, because a cap enforced in only two of the three layers is a cap that fails
 * in whichever layer was forgotten.
 */
export const personStatusSchema = z.enum(['active', 'blocked', 'archived']);
/**
 * Where a person is.
 *
 * Declared here rather than in `presence.ts` because that is where it lives: AD-29 puts the state
 * on the person row, which is the row AD-20 locks to decide a movement. Declaring it beside the
 * presence event instead created an import cycle between the two modules, and an import cycle in
 * ESM does not fail loudly — it leaves one of the two schemas `undefined` at module-evaluation
 * time and produces a confusing runtime error from inside the validation library. The surface test
 * caught exactly that.
 */
export const presenceStateSchema = z.enum(['inside', 'outside']);
/**
 * The state a record is created in, which is the state it holds until a movement says otherwise.
 *
 * **This facility is a residence**: people live inside and go out for a while, so a registration
 * records somebody who is present. The value is declared here, once, because it has to be true in
 * two places that cannot see each other — the `persons.presence_state` column default, and the
 * state `presence/service.ts` assumes when a backfill is decided against an instant no event
 * precedes. Two literals that must agree and are compared by nothing is how they drift; the
 * migration's own `SET DEFAULT` stays independently written so the schema test still triangulates
 * the running default rather than comparing it with itself.
 */
export const INITIAL_PRESENCE_STATE = 'inside';
/** How a stored image got here, which the audit trail and the print layout both care about. */
export const captureMethodSchema = z.enum(['upload', 'webcam']);
/**
 * The media types this system will decode, and therefore the ones a browser may offer to send.
 *
 * **It is here rather than beside the sniffer because two programs need the same list.** The server
 * decides what an upload IS by sniffing its magic bytes and never by what a client claimed (AD-52),
 * so this list is not a security control on the browser's side; what it is, is the `accept`
 * attribute on the file picker and the refusal an operator gets before spending a minute uploading
 * a file that was never going to be stored. A second copy in the browser would drift into offering a
 * format the server refuses — which is a wasted upload and a confusing refusal — or refusing one it
 * accepts, which is a capability nobody can reach. `packages/server/src/images/validate.ts`
 * re-exports it as `ACCEPTED_INPUT_MIMES` and states why HEIC is absent and AVIF is present.
 *
 * Keyed on the media type rather than on a file extension, because `file-type` collapses every
 * HEIF-family brand to the `heic` extension regardless of codec while keeping the media types
 * distinct.
 */
export const ACCEPTED_IMAGE_MEDIA_TYPES = Object.freeze([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/avif',
]);
export const personImageSchema = strict({
    id: uuidSchema,
    mime: z.string().max(80),
    bytes: z.int().positive(),
    width: z.int().positive(),
    height: z.int().positive(),
    position: z
        .int()
        .min(0)
        .max(MAX_ACTIVE_IMAGES_PER_PERSON - 1),
    capturedVia: captureMethodSchema,
    status: z.enum(['active', 'removed']),
    createdAt: isoDateTimeSchema,
});
/**
 * How many thumbnails one batched read may ask for.
 *
 * **A quarter of the route's thumbnail ceiling, so four batches and one record fit at once.** The
 * server decrypts at most `MAX_PAGE_SIZE + MAX_ACTIVE_IMAGES_PER_PERSON` thumbnails at a time and
 * keeps the last `MAX_ACTIVE_IMAGES_PER_PERSON` of those for requests no larger than one record's
 * photographs, so the gate's single face is never refused because a list filled the rest: four
 * batches of twenty-five plus that reserve is exactly the hundred and five. A register page, twenty
 * rows, is therefore one request.
 */
export const MAX_THUMBNAILS_PER_REQUEST = 25;
/**
 * The image ids one batched read names, as ONE comma-separated query parameter.
 *
 * **A transform rather than a preprocess, and the difference is what the API document says.** The
 * OpenAPI emitter documents a transform by its INPUT — a string, which is what a caller writes —
 * and a preprocess by its OUTPUT, an array no query string can carry. The bound and the duplicate
 * check run INSIDE the transform, before any element is parsed, because Zod keeps running element
 * checks, array bounds and refinements after an element has failed: thirty malformed ids would
 * otherwise produce thirty details and a spurious duplicate complaint on top of them.
 *
 * The bound's issue declares no message on purpose: `origin: 'array'` is what makes the detail
 * mapper choose `errors.validation.too_many_items` and fill its `{{max}}`, and a message declared
 * here would bypass that and leave the placeholder unfilled. Case is folded first, because two
 * spellings of one uuid name one image and PostgreSQL compares them as equal.
 */
const thumbnailIdListSchema = z
    .string()
    .transform((raw, context) => {
    const ids = raw.toLowerCase().split(',');
    if (ids.length > MAX_THUMBNAILS_PER_REQUEST) {
        context.issues.push({
            code: 'too_big',
            origin: 'array',
            maximum: MAX_THUMBNAILS_PER_REQUEST,
            inclusive: true,
            input: raw,
        });
        return z.NEVER;
    }
    if (new Set(ids).size !== ids.length) {
        context.issues.push({
            code: 'custom',
            input: raw,
            message: 'errors.validation.invalid_format',
        });
        return z.NEVER;
    }
    return ids;
})
    .pipe(z.array(uuidSchema).min(1));
/** `GET /api/v1/person-thumbnails`'s query: the image ids, and nothing else. */
export const personThumbnailsQuerySchema = strict({ ids: thumbnailIdListSchema });
/**
 * One thumbnail, as bytes a browser can draw without a second request.
 *
 * `jpegBase64` rather than `data`, because `data` is already the member every realtime frame
 * carries, and a capture or a log reading one would read the other. Every stored thumbnail is a
 * JPEG re-encoded by the server (`images/pipeline.ts`), which is why the media type is a literal.
 */
export const personThumbnailSchema = strict({
    imageId: uuidSchema,
    mediaType: z.literal('image/jpeg'),
    jpegBase64: z.base64(),
});
/**
 * A batched read's answer: what was served, what does not exist, and what could not be read.
 *
 * **Three lists rather than one with a status, and the split is decided by the ROW.** `missing`
 * means there is nothing to show and never will be: no such image, a removed one, or a person
 * whose record was erased. `unavailable` means the row says a photograph exists and it could not
 * be produced — the store did not answer, or answered that the object is gone, which is what a
 * database restored without its photographs looks like. The split tells a caller whether asking
 * again can help: `missing` never will, `unavailable` may. The browser retries only the second, and
 * draws BOTH as "could not be loaded" rather than as "no photograph", because the list that named
 * the image said a photograph exists.
 */
export const personThumbnailBatchSchema = strict({
    items: z.array(personThumbnailSchema).max(MAX_THUMBNAILS_PER_REQUEST),
    missing: z.array(uuidSchema).max(MAX_THUMBNAILS_PER_REQUEST),
    unavailable: z.array(uuidSchema).max(MAX_THUMBNAILS_PER_REQUEST),
});
/**
 * An operator's ad-hoc field: a name and a value, for this person only.
 *
 * Distinct from an admin-defined field, which renders on every person's form. These are the
 * "additional fields" of the brief, capped at ten and free to differ from record to record.
 */
export const adhocFieldSchema = strict({
    name: z.string().trim().min(1, { error: 'errors.validation.required' }).max(120),
    value: z.string().trim().max(500),
});
export const adhocFieldsSchema = z
    .array(adhocFieldSchema)
    .max(MAX_ADHOC_FIELDS_PER_PERSON, { error: 'errors.person.adhoc_field_limit_reached' })
    .refine((fields) => new Set(fields.map((field) => field.name)).size === fields.length, {
    // Its own key, not `errors.person.duplicate_unique_value`. That message names the field it is
    // about — `«{{fieldKey}}»` — and an ad-hoc field has no machine key to name: what it has is a
    // name an operator typed, which must never be interpolated into a message that reaches a log.
    // Sharing the key also rendered the placeholder LITERALLY here, because this path supplies no
    // params at all.
    error: 'errors.person.adhoc_name_duplicate',
});
/**
 * Builds the validator for a submitted field map from the active definitions.
 *
 * Unknown keys are refused rather than dropped, retired fields are refused on a create and
 * tolerated on a read, and a `required` definition with no value is refused. A `warn` definition
 * with no value is NOT refused: that is the completeness warning, and the brief is explicit that it
 * is a question the operator answers, never a block.
 */
export function personFieldsSchema(definitions, context) {
    const active = definitions.filter((definition) => definition.status === 'active');
    const shape = {};
    for (const definition of active) {
        const value = fieldValueSchema(definition, context);
        shape[definition.key] =
            definition.requirement === 'required' ? value : value.nullish().optional();
    }
    // The cross-field rules are applied HERE and nowhere else, because this is the only validator
    // that sees more than one value. Leaving them to a caller to remember would mean the composed
    // path accepted a passport that expires before its holder was born, which is exactly what the
    // acceptance criteria refuse.
    return z.strictObject(shape).superRefine((fields, ctx) => {
        for (const problem of checkCrossFieldRules(fields)) {
            ctx.addIssue({
                code: 'custom',
                message: problem.messageKey,
                path: [problem.fieldKey],
            });
        }
    });
}
export function checkCrossFieldRules(fields) {
    const problems = [];
    const birth = fields.date_of_birth;
    const expiry = fields.passport_expiry;
    if (typeof birth === 'string' && typeof expiry === 'string' && expiry < birth) {
        problems.push({
            fieldKey: 'passport_expiry',
            path: 'fields.passport_expiry',
            messageKey: 'errors.validation.expiry_before_birth',
        });
    }
    return problems;
}
/**
 * Somebody's age in whole years on a given day, or `null` when the date is not one.
 *
 * **Derived rather than stored, and that is the whole decision.** An age typed into a field is
 * wrong the day after it is entered and then disagrees with the date of birth sitting next to it,
 * so this product records the birthday and computes the number wherever one is shown.
 *
 * **Calendar arithmetic, not elapsed time.** `field.ts` has a `yearsBetween` that divides by a
 * 365.2425-day year, which is the right tool for the `maxAgeYears` BOUND it serves and the wrong
 * one here: a mean year is not a calendar year, so somebody born on 29 February, or simply
 * somebody whose birthday is today, can come out a year adrift. The rule below is the one a person
 * uses — subtract the years, then take one back if this year's birthday has not come round — and it
 * has no rounding to be wrong about.
 *
 * `today` is injected, never read from a clock, so a test pins it and so the answer is cut in the
 * installation's reporting timezone like every other date this product renders.
 */
export function ageOnDate(dateOfBirth, today) {
    if (!ISO_CALENDAR_DATE.test(dateOfBirth) || !ISO_CALENDAR_DATE.test(today)) {
        return null;
    }
    const years = Number(today.slice(0, 4)) - Number(dateOfBirth.slice(0, 4));
    // The month and day compare as text because `MM-DD` is fixed width and zero padded, so its
    // lexical order IS its calendar order. A birthday later in the year than today's date means this
    // year's has not happened yet.
    const age = today.slice(5) < dateOfBirth.slice(5) ? years - 1 : years;
    // A future date of birth is refused by the field's own `notFuture` rule, so reaching here with a
    // negative age means a record written before that rule existed. `null` rather than a negative
    // number, because "minus three" on a record screen is worse than no answer at all.
    return age < 0 ? null : age;
}
/** `YYYY-MM-DD`, which is the only shape a `date` field ever stores. */
const ISO_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/u;
/** The completeness warning: which of the watched fields this submission left empty. */
export const completenessWarningSchema = strict({
    missing: z.array(machineKeySchema),
});
/** Creating a person. Only first and last name are ever required; the rest is warned about. */
export const createPersonRequestSchema = strict({
    fields: z.record(machineKeySchema, z.unknown()),
    adhocFields: adhocFieldsSchema.default([]),
    /** The operator has seen the completeness warning and chosen to continue. */
    acceptIncomplete: z.boolean().default(false),
    /** Present when `consent.required` is on; recorded with the notice version. */
    consentAcknowledged: z.boolean().optional(),
    imageIds: z
        .array(uuidSchema)
        .max(MAX_ACTIVE_IMAGES_PER_PERSON, { error: 'errors.person.image_limit_reached' })
        .default([]),
    /**
     * The code off the pre-printed band the operator scanned (AD-60).
     *
     * **Optional in the SCHEMA and decided by the installation**, which is the only place the rule
     * can live: this package is shared with the browser and knows nothing about `PASS_ISSUANCE_MODE`,
     * and the same field must be absent in `on_demand` mode, where submitting a code is refused. What
     * an absent code means is therefore the credential service's answer, it being the one authority on
     * how a pass comes into existence — and in `preprinted` mode it reads
     * `passes.no_band_at_registration`: `refuse` rolls the whole registration back with
     * `CREDENTIAL_CODE_REQUIRED`, `emergency` mints a paper pass for the day the box is empty, and
     * `defer` registers the person with no pass at all and requires a reference code instead.
     */
    passCode: passCodeSchema.optional(),
    /** AD-53: how this registration was reached. Absent means the manual path; see the constant. */
    method: recordActionMethodSchema.optional(),
    idempotencyKey: idempotencyKeySchema,
});
/** Amending a person. `version` is what makes a concurrent edit a conflict rather than a loss. */
export const updatePersonRequestSchema = strict({
    fields: z.record(machineKeySchema, z.unknown()).optional(),
    adhocFields: adhocFieldsSchema.optional(),
    acceptIncomplete: z.boolean().default(false),
    status: personStatusSchema.optional(),
    reason: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
    /** AD-53, exactly as on a create. */
    method: recordActionMethodSchema.optional(),
    version: versionSchema,
    idempotencyKey: idempotencyKeySchema,
});
/**
 * Why a record was erased, as a closed set.
 *
 * **It is a closed set because the ledger row survives the erasure and is the only thing that
 * does.** `shred_ledger.reason_code` is cleartext by design — it carries no personal data — and it
 * is what an auditor reads years later to answer "why is this person gone". A free-form token there
 * would be four spellings of one reason across four operators, and no SQL reader could group them:
 * this route already had two in its own tests, `subject_request` and `data_subject_request`.
 *
 * **`retention_policy` is in the set and is not offered to a person.** It is the reason the nightly
 * retention job gives, and the job reaches the service directly rather than through this schema;
 * naming it here is what stops a second spelling of it appearing when somebody writes the report
 * that counts erasures by reason. {@link OPERATOR_SELECTABLE_SHRED_REASONS} is what a picker
 * renders, and the type excludes it, so a screen that tried to offer it would not compile.
 */
export const SHRED_REASON_CODES = [
    /** The person asked. The commonest reason, and the one with a legal clock on it. */
    'data_subject_request',
    /** The retention job, which no operator performs. */
    'retention_policy',
    /** The record should never have existed: a duplicate, or somebody entered in error. */
    'erroneous_record',
    /** An instruction from outside this facility that the record be destroyed. */
    'legal_order',
];
export const shredReasonCodeSchema = z.enum(SHRED_REASON_CODES);
/** The reasons a PERSON may choose. The job's own is deliberately not one of them. */
export const OPERATOR_SELECTABLE_SHRED_REASONS = SHRED_REASON_CODES.filter((code) => code !== 'retention_policy');
/**
 * Destroying a person's data (AD-10).
 *
 * The typed confirmation is the `short_ref`, so the operation cannot be completed by clicking
 * through a dialog: the person doing it has to have the record in front of them.
 */
export const shredPersonRequestSchema = strict({
    confirmation: shortRefSchema,
    reasonCode: shredReasonCodeSchema,
});
/** The list row: what search returns, and the smallest thing that is still useful at a gate. */
export const personSummarySchema = strict({
    id: uuidSchema,
    shortRef: shortRefSchema,
    status: personStatusSchema,
    presenceState: presenceStateSchema,
    displayName: z.string(),
    /**
     * The facility's own reference code for this record, or `null` when it holds none.
     *
     * **Served from the search projection, exactly as `displayName` is**, so a list page costs no
     * extra decryption: the value is already in the process that renders the row. It is personal data
     * — an identifier that names one record — so it is dropped by NAME for a machine caller that does
     * not hold `allow_pii`, rather than being left to the shape check, which would keep a reference
     * like `a001` and drop one like `0001`.
     */
    referenceCode: z.string().nullable(),
    primaryImageId: uuidSchema.nullable(),
    lastActivityAt: isoDateTimeSchema.nullable(),
});
/** The full record, decrypted for an authorised reader. */
export const personDetailSchema = strict({
    id: uuidSchema,
    shortRef: shortRefSchema,
    status: personStatusSchema,
    presenceState: presenceStateSchema,
    fields: z.record(machineKeySchema, z.unknown()),
    adhocFields: z.array(adhocFieldSchema),
    images: z.array(personImageSchema),
    consentAckAt: isoDateTimeSchema.nullable(),
    consentNoticeVersion: z.int().nullable(),
    completeness: completenessWarningSchema,
    lastActivityAt: isoDateTimeSchema.nullable(),
    shreddedAt: isoDateTimeSchema.nullable(),
    version: versionSchema,
    audit: auditStampSchema,
});
/** One point in the record's history (AD-25). The snapshot itself is fetched separately. */
export const personRevisionSchema = strict({
    id: uuidSchema,
    revision: z.int().positive(),
    changedFieldKeys: z.array(machineKeySchema),
    actorId: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
/** What a record read says about how it was reached (AD-53). */
export const personDetailQuerySchema = strict({
    /**
     * AD-53, on the one record action that has no body to carry it.
     *
     * Reading a whole record is the action AD-37's exfiltration threshold counts, and the
     * manual-versus-scan ratio is counted over the same rows, so the method has to travel even
     * though the request is a GET. A query parameter is safe here in a way it is not for a pass code
     * or a name: the value is one of five closed tokens and says nothing about anybody.
     */
    method: recordActionMethodSchema.optional(),
});
/**
 * The columns the register may be ordered by.
 *
 * **A closed set rather than a free column name**, because every member has to be answerable
 * without decrypting anything: four come from the search projection, and the last two are the
 * cleartext lifecycle columns `persons` already holds. A field the projection does not hold would
 * have to open every record in the installation to order one page, which is the opposite of what
 * the projection exists for. The values are the register table's own column ids, so a view shared as
 * a link and the query string it produces cannot spell the same column two ways.
 *
 * **`referenceCode` is answerable for the same reason `displayName` is** — the projection holds it
 * decrypted already — and a record with none sorts AFTER every record that has one in BOTH
 * directions, exactly as `lastActivityAt` does for somebody who has never moved: "no reference" is
 * not a code and does not lead a list of codes.
 */
export const personSortKeySchema = z.enum([
    'shortRef',
    'displayName',
    'referenceCode',
    'status',
    'presenceState',
    'lastActivityAt',
]);
/** Whether a row must have a photograph on file. Absent means "either", never "both". */
export const personPhotoFilterSchema = z.enum(['with', 'without']);
/**
 * One page of the register, and the one thing a page of SEARCH results has to say that a page of
 * anything else does not.
 *
 * **`refine` is on this response and not on the shared envelope, deliberately.** It is a property of
 * searching rather than of paging: what it means is that more people matched than one response may
 * carry, so the list is a prefix of the answer and the honest next step is a narrower query rather
 * than another page. It is exactly the gap between `total` and `pageCount * limit`, stated rather
 * than left for a client to derive — and without it a capped list is indistinguishable from a
 * complete one, which is the "truncated list presented as complete" the search engine's own cap
 * exists to avoid. A query-free listing is paged over the whole register and is never cut short, so
 * there `refine` is always false and `pageCount` alone tells the truth.
 */
export const personPageSchema = numberedPageSchema(personSummarySchema).extend({
    refine: z.boolean(),
});
/**
 * What a register page asks for: one query box, four narrowings, an ordering and a page.
 *
 * **One `q` rather than a field per column** (Task 10.2): the operator types a name, half a name, a
 * misspelled name, a passport number with or without its separators, an e-mail address or the last
 * digits of a telephone, and the same box answers all of them. A form with a box per field would
 * make the person at the desk choose which kind of thing they are holding before they can look
 * anybody up.
 *
 * **Every narrowing here is applied by the SERVER, over everybody, before the answer is counted and
 * cut into pages.** `status` and `country` are answered from the projection and decrypt nothing;
 * `presence` and `photo` are answered from the cleartext lifecycle columns. A narrowing applied to
 * the rows that came back instead is a different statement about a different set, and reading the
 * second as the first is how "no matches" gets believed about somebody who is registered.
 * Completeness and passport-expiry buckets are still NOT offered: `personSummarySchema` carries
 * neither, and a filter over them would have to decrypt every record to answer one page.
 *
 * **It carries `page` rather than a cursor, and it is the only list route in this product that
 * does.** `persons/list.ts` holds the argument: the order is the reader's own choice out of five
 * columns and two directions, so there is no key a cursor could resume after; the set is in memory,
 * so a page is a slice rather than a walk over discarded rows; and the register's view lives in the
 * URL, where a keyset cursor would spell out the displayed NAME of the last row on the page.
 *
 * **`direction` is never ignored.** With a `sort` it is that column's direction; with none it
 * reverses whichever order the route is serving — the name order of a listing, the relevance order
 * of a search. A known parameter that is accepted and quietly dropped is the defect `common.ts`
 * states `noQuerySchema` exists to prevent, one field over.
 */
export const personListQuerySchema = paginationQuerySchema.omit({ cursor: true }).extend({
    q: z.string().trim().max(200).optional(),
    status: personStatusSchema.optional(),
    country: countryCodeSchema.optional(),
    presence: presenceStateSchema.optional(),
    photo: personPhotoFilterSchema.optional(),
    sort: personSortKeySchema.optional(),
    page: z.coerce.number().int().min(1).max(1_000_000).default(1),
});
/**
 * The as-you-type duplicate guard's request (AD-18).
 *
 * **A POST with the values in the body, never a query string**, for the reason AD-28 gives for a
 * pass code: a name, a passport number and a country in a URL are personal data written in
 * cleartext into two access logs, a browser history and any `Referer`, all outside the encryption
 * boundary and rotated rather than shredded.
 *
 * The values are whatever has been typed so far, so the map is deliberately loose: a form being
 * filled in is by definition not yet valid, and a warning that only appeared once it was would
 * appear after the operator had finished entering the duplicate.
 */
export const duplicateCandidatesRequestSchema = strict({
    fields: z.record(machineKeySchema, z.unknown()),
    /** The record being edited, which must never be offered as a duplicate of itself. */
    excludePersonId: uuidSchema.optional(),
});
/**
 * Which check produced a duplicate candidate, so the panel can say why it is showing this record.
 *
 * **Named and exported rather than written inline, because two programs must be total over it.** The
 * server decides the signals and the browser renders one sentence per signal from a `Record` keyed
 * by this union — and a `Record` can only be proved total against a name. An inline `z.enum` here
 * left the browser mapping with a two-branch ternary, which silently rendered a third signal as the
 * second one's sentence.
 *
 * The declaration still has to be kept in step with `persons/duplicates.ts`'s own union by hand,
 * and the failure mode of not doing so is worth knowing: a server sending a signal this schema does
 * not admit produces a body the browser `safeParse`s away into a blank panel rather than an error.
 */
export const duplicateSignalSchema = z.enum(['fingerprint', 'passport', 'reference']);
/** One candidate, with nothing decrypted to produce it. */
export const duplicateCandidateSchema = strict({
    personId: uuidSchema,
    shortRef: shortRefSchema,
    status: personStatusSchema,
    presenceState: presenceStateSchema,
    primaryImageId: uuidSchema.nullable(),
    /** Every signal that matched, so a record matching more than one reads as the stronger warning. */
    matchedOn: z.array(duplicateSignalSchema).min(1),
});
/**
 * Replacing a person's ad-hoc fields.
 *
 * The whole set at once rather than one at a time, because that is what the domain does: the person
 * service takes a list and replaces it, and a per-row API over a wholesale write would be two
 * clients disagreeing about what "delete field three" means when field three moved. `version` is
 * what makes a concurrent edit a conflict rather than a lost one, exactly as it is on an amendment.
 */
export const replaceAdhocFieldsRequestSchema = strict({
    adhocFields: adhocFieldsSchema,
    version: versionSchema,
});
/**
 * A webcam capture: one frame, base64, with no data-URL prefix.
 *
 * The prefix is refused rather than stripped. `data:image/png;base64,` is a claim about the format
 * made by the caller, and this system decides a format by sniffing magic bytes and never by what a
 * client said (AD-52); accepting the prefix would put a string in the request that looks
 * authoritative and is ignored, which is how somebody later "fixes" the pipeline to trust it.
 */
export const captureImageRequestSchema = strict({
    frame: z
        .string()
        .min(1, { error: 'errors.validation.required' })
        .regex(/^[A-Za-z0-9+/]+={0,2}$/u, { error: 'errors.person.image_rejected' }),
});
/**
 * What a create answers with.
 *
 * **Two outcomes, and the incomplete one is a SUCCESS.** Section 1.2's first behavioural example is
 * an operator registering somebody with only a name, being warned about what is missing, and
 * confirming anyway; returning a failure for that would make the ordinary path an error case and
 * leave the client distinguishing it from a real refusal by its code. So `confirmation_required` is
 * a 200 carrying the warning and the duplicates, and the client re-submits with `acceptIncomplete`.
 *
 * Duplicates ride on both branches and gate neither: blocking on a colliding passport is the exact
 * failure mode AD-18 exists to prevent.
 */
export const createPersonResponseSchema = z.discriminatedUnion('kind', [
    strict({
        kind: z.literal('confirmation_required'),
        completeness: completenessWarningSchema,
        duplicates: z.array(duplicateCandidateSchema),
    }),
    strict({
        kind: z.literal('created'),
        person: personDetailSchema,
        completeness: completenessWarningSchema,
        duplicates: z.array(duplicateCandidateSchema),
        /**
         * The pass this registration issued, or `null` when it issued none.
         *
         * **`null` is reachable two ways, and they are different situations.**
         *
         * An **API key** always gets it: AD-34 lets no scope reach a credential, so a machine registers
         * people and never obtains a pass — deliberate pre-registration, which the desk completes by
         * binding a band from the person's record.
         *
         * An **operator** gets it only where the installation says so. In `on_demand` mode the server
         * mints, and in `preprinted` mode `passes.no_band_at_registration` decides: `refuse` rolls the
         * whole registration back without a band (`CREDENTIAL_CODE_REQUIRED`), `emergency` mints a
         * paper pass, and `defer` answers `null` — the person is registered with no pass and the band is
         * bound later from their own record. `defer` requires a reference code in the same request
         * (`PERSON_REFERENCE_REQUIRED`), so a `null` here never means nobody can find the record again.
         *
         * The sentence this replaced said `null` was "reachable by exactly one caller", which is the
         * kind of invariant a later reader trusts: it is recorded here that it stopped being true rather
         * than left standing.
         */
        credential: issuedCredentialSchema.nullable(),
    }),
]);
/**
 * What an amendment answers with.
 *
 * The confirmation branch is narrower than a create's on purpose: an amendment asks only when the
 * edit makes the record LESS complete than it already was. Gating on the whole warning would demand
 * an acknowledgment on every edit of every incomplete record, and a record registered without a
 * photograph stays without one until somebody adds it.
 */
export const amendPersonResponseSchema = z.discriminatedUnion('kind', [
    strict({
        kind: z.literal('confirmation_required'),
        completeness: completenessWarningSchema,
        newlyMissing: z.array(machineKeySchema).min(1),
    }),
    strict({
        kind: z.literal('amended'),
        person: personDetailSchema,
        completeness: completenessWarningSchema,
        revision: z.int().positive(),
        changedFieldKeys: z.array(machineKeySchema),
    }),
]);
/**
 * What an erasure answers with (AD-10).
 *
 * `already_erased` is an outcome rather than an error, because a second shred of the same person is
 * specified as a no-op: an erasure is requested through a confirmation dialog, and a retried
 * submission must not produce a second ledger row for one act.
 *
 * Every number here is a count and every string is an id or a record reference. Nothing about the
 * person who was erased survives in this body, which is the point of having erased them.
 */
export const shredPersonResponseSchema = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('already_erased'), personId: uuidSchema, shortRef: shortRefSchema }),
    strict({
        kind: z.literal('erased'),
        personId: uuidSchema,
        shortRef: shortRefSchema,
        shredLedgerId: uuidSchema,
        valuesDeleted: z.int().nonnegative(),
        adhocDeleted: z.int().nonnegative(),
        imagesDeleted: z.int().nonnegative(),
        credentialsRevoked: z.int().nonnegative(),
        objectsDeleted: z.int().nonnegative(),
        /** Objects the store would not release. Named as keys, which carry nothing about anybody. */
        objectsLeftBehind: z.array(z.string()),
        executedAt: isoDateTimeSchema,
    }),
]);
//# sourceMappingURL=person.js.map